import React, { useState, useRef } from "react";
import {
  Box,
  useTheme,
  TextField,
  Popper,
  Paper,
  ClickAwayListener,
  IconButton,
  Divider,
  Button,
  Typography,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDownRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import { getGridButtonSelectorStyles } from "../theme";
import {
  POPPER_CONFIG,
  PAPER_CONFIG,
  DATE_RANGE_CONFIG,
  GRID_SIZES,
  SPACING,
  TRANSITIONS,
} from "../constants/filterSelectors";

const DashboardFilterSelector = ({
  // Service selection
  selectedPage,
  onPageChange,
  dashboardPagesTitles,

  // View/Overview selection
  selectedView,
  onViewChange,
  dashboardPagesViews,
  selectedOverview,
  onOverviewChange,
  dashboardOverviews,

  // Date selection
  dateSelection,
  onDateSelectionSave,
  months,
  years,
  currentYear,

  // Helper functions
  isAllServiceSelected,
  shouldShowDatePicker,
  shouldAllowMultiMonth,
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const textFieldRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Local state for unsaved changes
  const [localPage, setLocalPage] = useState(selectedPage);
  const [localView, setLocalView] = useState(selectedView);
  const [localOverview, setLocalOverview] = useState(selectedOverview);
  const [localDateSelection, setLocalDateSelection] = useState(dateSelection);

  const handleClick = () => {
    setAnchorEl(anchorEl ? null : textFieldRef.current);
  };

  const handleClose = () => {
    // Reset to saved values
    setLocalPage(selectedPage);
    setLocalView(selectedView);
    setLocalOverview(selectedOverview);
    setLocalDateSelection(dateSelection);
    setAnchorEl(null);
  };

  const handleSave = () => {
    // Save all changes
    if (localPage !== selectedPage) {
      onPageChange(localPage);
    }
    if (localView !== selectedView) {
      onViewChange(localView);
    }
    if (localOverview !== selectedOverview) {
      onOverviewChange(localOverview);
    }
    if (JSON.stringify(localDateSelection) !== JSON.stringify(dateSelection)) {
      onDateSelectionSave(localDateSelection);
    }
    setAnchorEl(null);
  };

  const getDisplayText = () => {
    const serviceName = dashboardPagesTitles[localPage];
    const isAllSelected = isAllServiceSelected(localPage);

    if (isAllSelected) {
      return `${serviceName} • ${localOverview}`;
    } else {
      return `${serviceName} • ${localView}`;
    }
  };

  const open = Boolean(anchorEl);
  const isApplyDisabled =
    shouldShowDatePicker(localPage, localView, localOverview) &&
    localDateSelection.months.length === 0;

  return (
    <Box
      sx={{ position: "relative", width: isMobile ? "auto" : "100%" }}
      ref={textFieldRef}
    >
      {isMobile ? (
        <Tooltip title="Dashboard Filters">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.background.default,
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "12px",
              border: "none",
              color: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHover,
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <FilterListRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <TextField
          fullWidth
          size="small"
          label="Dashboard Filters"
          value={getDisplayText()}
          onClick={handleClick}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none !important",
            },
            height: "100%",
            "& .MuiInputBase-root": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              boxShadow:
                "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
            },
            "& .MuiInputBase-input": {
              py: "8px !important",
              color: theme.palette.text.primary,
              fontSize: "0.875rem",
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton
                edge="end"
                onClick={handleClick}
                sx={{ padding: 0.5 }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            ),
          }}
        />
      )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{
          zIndex: POPPER_CONFIG.zIndex,
          width: isMobile ? "calc(100vw - 32px)" : undefined,
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={PAPER_CONFIG.elevation}
            sx={{
              p: isMobile ? 1.5 : PAPER_CONFIG.padding,
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.background.default}`,
              borderRadius: PAPER_CONFIG.borderRadius,
              minWidth: isMobile ? "auto" : PAPER_CONFIG.minWidth,
              maxWidth: isMobile ? "calc(100vw - 32px)" : "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: SPACING.containerGap,
              }}
            >
              {/* Left Side - Date Range Selection */}
              <Box
                sx={{
                  width: { xs: "100%", sm: DATE_RANGE_CONFIG.width },
                  opacity: shouldShowDatePicker(
                    localPage,
                    localView,
                    localOverview,
                  )
                    ? 1
                    : 0.4,
                  pointerEvents: shouldShowDatePicker(
                    localPage,
                    localView,
                    localOverview,
                  )
                    ? "auto"
                    : "none",
                  transition: TRANSITIONS.opacity,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: theme.palette.primary.text2,
                  }}
                >
                  Date Range
                </Typography>

                {/* Months Selection */}
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: theme.palette.text.secondary,
                  }}
                >
                  Months
                </Typography>

                {/* Select All/None buttons - only show in multi-month mode */}
                {shouldAllowMultiMonth(localPage) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Button
                      onClick={() => {
                        setLocalDateSelection((prev) => ({
                          ...prev,
                          months: [...months],
                        }));
                      }}
                      size="small"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={() => {
                        setLocalDateSelection((prev) => ({
                          ...prev,
                          months: [],
                        }));
                      }}
                      size="small"
                      sx={{ color: theme.palette.primary.main }}
                    >
                      Select None
                    </Button>
                  </Box>
                )}

                <Grid2 container spacing={SPACING.gridSpacing} sx={{ mb: 2 }}>
                  {months.map((month) => {
                    const isSelected =
                      localDateSelection.months.includes(month);
                    const isMultiMonth = shouldAllowMultiMonth(localPage);

                    return (
                      <Grid2 key={month} size={GRID_SIZES.month}>
                        <Box
                          sx={getGridButtonSelectorStyles(theme, isSelected)}
                          onClick={() => {
                            if (isMultiMonth) {
                              // Multi-month selection
                              if (isSelected) {
                                setLocalDateSelection((prev) => ({
                                  ...prev,
                                  months: prev.months.filter(
                                    (m) => m !== month,
                                  ),
                                }));
                              } else {
                                setLocalDateSelection((prev) => ({
                                  ...prev,
                                  months: [...prev.months, month],
                                }));
                              }
                            } else {
                              // Single month selection
                              setLocalDateSelection((prev) => ({
                                ...prev,
                                months: [month],
                              }));
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: isSelected
                                ? "#ffffff"
                                : theme.palette.primary.main,
                              fontWeight: isSelected ? "bold" : "normal",
                            }}
                          >
                            {month}
                          </Typography>
                        </Box>
                      </Grid2>
                    );
                  })}
                </Grid2>

                {/* Years Selection */}
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: theme.palette.text.secondary,
                  }}
                >
                  Year
                </Typography>
                <Grid2 container spacing={1}>
                  {(years.length > 0 ? years : [currentYear]).map((year) => {
                    const isSelected = localDateSelection.years.includes(year);

                    return (
                      <Grid2 key={year} size={GRID_SIZES.year}>
                        <Box
                          sx={getGridButtonSelectorStyles(theme, isSelected)}
                          onClick={() => {
                            // Single year selection only
                            setLocalDateSelection((prev) => ({
                              ...prev,
                              years: [year],
                            }));
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: isSelected
                                ? "#ffffff"
                                : theme.palette.primary.main,
                              fontWeight: isSelected ? "bold" : "normal",
                            }}
                          >
                            {year}
                          </Typography>
                        </Box>
                      </Grid2>
                    );
                  })}
                </Grid2>
              </Box>

              <Divider
                orientation={{ xs: "horizontal", sm: "vertical" }}
                flexItem
              />

              {/* Right Side - Service and View/Overview */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: SPACING.verticalGap,
                }}
              >
                {/* Service Selection */}
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.primary.text2,
                    }}
                  >
                    Service
                  </Typography>
                  <Grid2 container spacing={SPACING.gridSpacing}>
                    {dashboardPagesTitles.map((service, index) => {
                      const isSelected = localPage === index;
                      return (
                        <Grid2 key={service} size={GRID_SIZES.twoColumn}>
                          <Box
                            sx={{
                              ...getGridButtonSelectorStyles(theme, isSelected),
                              py: SPACING.buttonPadding,
                              minHeight: "auto",
                            }}
                            onClick={() => {
                              setLocalPage(index);
                              // If ALL is selected, set the overview to General Overview
                              if (
                                index === dashboardPagesTitles.indexOf("ALL")
                              ) {
                                setLocalOverview("General Overview");
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: isSelected
                                  ? "#ffffff"
                                  : theme.palette.primary.main,
                                fontWeight: isSelected ? "bold" : "normal",
                              }}
                            >
                              {service}
                            </Typography>
                          </Box>
                        </Grid2>
                      );
                    })}
                  </Grid2>
                </Box>

                <Divider />

                {/* View or Overview Selection */}
                {isAllServiceSelected(localPage) ? (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      Overview
                    </Typography>
                    <Grid2 container spacing={SPACING.gridSpacing}>
                      {dashboardOverviews.map((overview) => {
                        const isSelected = localOverview === overview;
                        return (
                          <Grid2 key={overview} size={GRID_SIZES.twoColumn}>
                            <Box
                              sx={{
                                ...getGridButtonSelectorStyles(
                                  theme,
                                  isSelected,
                                ),
                                py: SPACING.buttonPadding,
                                minHeight: "auto",
                              }}
                              onClick={() => setLocalOverview(overview)}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isSelected
                                    ? "#ffffff"
                                    : theme.palette.primary.main,
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                              >
                                {overview}
                              </Typography>
                            </Box>
                          </Grid2>
                        );
                      })}
                    </Grid2>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mb: 1,
                        fontWeight: "bold",
                        color: theme.palette.primary.text2,
                      }}
                    >
                      View
                    </Typography>
                    <Grid2 container spacing={SPACING.gridSpacing}>
                      {dashboardPagesViews.map((view) => {
                        const isSelected = localView === view;
                        return (
                          <Grid2 key={view} size={GRID_SIZES.twoColumn}>
                            <Box
                              sx={{
                                ...getGridButtonSelectorStyles(
                                  theme,
                                  isSelected,
                                ),
                                py: SPACING.buttonPadding,
                                minHeight: "auto",
                              }}
                              onClick={() => setLocalView(view)}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isSelected
                                    ? "#ffffff"
                                    : theme.palette.primary.main,
                                  fontWeight: isSelected ? "bold" : "normal",
                                }}
                              >
                                {view}
                              </Typography>
                            </Box>
                          </Grid2>
                        );
                      })}
                    </Grid2>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: 2 }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: SPACING.gridSpacing,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: PAPER_CONFIG.borderRadius,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isApplyDisabled}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: PAPER_CONFIG.borderRadius,
                  "&:hover": {
                    bgcolor: theme.palette.primary.buttonHoverSelected,
                  },
                  ...(isApplyDisabled && {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                    cursor: "not-allowed",
                  }),
                }}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default DashboardFilterSelector;
